import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, Users, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Somasi Ops — Corporate Services Management" },
      {
        name: "description",
        content:
          "A simple operational tool for Somasi Corporate Services — track BVI companies, contacts and billing in one calm place.",
      },
      { property: "og:title", content: "Somasi Ops — Corporate Services Management" },
      {
        property: "og:description",
        content:
          "A simple operational tool for Somasi Corporate Services — track BVI companies, contacts and billing in one calm place.",
      },
    ],
  }),
  component: Landing,
});

function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <img src="/somasi-mark.png" alt="" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="text-sm font-semibold">Somasi Ops</div>
              <div className="text-[11px] text-muted-foreground">Corporate Services</div>
            </div>
          </div>
          <Button asChild size="sm">
            <Link to="/auth">Sign in</Link>
          </Button>
        </div>
      </header>

      <section className="mx-auto max-w-4xl px-6 py-20 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border bg-secondary/50 px-3 py-1 text-xs text-secondary-foreground">
          <Sparkles className="h-3 w-3 text-primary" />
          Complimentary operational tool
        </div>
        <h1 className="mt-6 text-4xl font-semibold tracking-tight sm:text-5xl">
          A calm home for Somasi's client and company records.
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground">
          Client groups, BVI companies, contact roles and billing information — organised in one
          place, maintained in the background by your outsourced team, ready when you need it.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg">
            <Link to="/auth">
              Sign in to continue <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <section className="mx-auto grid max-w-5xl gap-6 px-6 pb-24 sm:grid-cols-3">
        <FeatureCard
          icon={Users}
          title="Client & contact master"
          text="One tidy place for every client group, contact person and their role — no more scattered spreadsheets."
        />
        <FeatureCard
          icon={Building2}
          title="BVI company register"
          text="Track legal name, registration, jurisdiction, AFR status and QuickBooks mapping for each company."
        />
        <FeatureCard
          icon={ShieldCheck}
          title="Maintained for you"
          text="Your outsourced accounting team handles imports, updates and data quality quietly in the background."
        />
      </section>

      <footer className="border-t py-8 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Somasi Corporate Services
      </footer>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Building2;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-base font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
